<template>
  <div v-if="visible">
    <settings-group-label>זיהוי שיחות נכנסות</settings-group-label>
    <settings-card>
      <settings-toggle-row
        title="זיהוי לקוחות בשיחה נכנסת"
        :description="mainDescription"
        :status="settings.enabled"
        :disabled="busy || (status !== null && !status.supported && !settings.enabled)"
        :last="!settings.enabled"
        @value-changed="onToggleEnabled"
      />

      <template v-if="settings.enabled">
        <!-- Android: up to four grants. Only the call-screening role is required. -->
        <template v-if="platform === 'android' && status?.supported">
          <setup-step
            title="הגדרת TimeIt כאפליקציית זיהוי שיחות"
            description="חובה — כך אנדרואיד מעביר לנו את המספר כשמישהו מתקשר"
            :done="!!status?.screeningRole"
            :loading="busyStep === 'role'"
            @action="runStep('role', () => CallerId.requestScreeningRole())"
          />
          <setup-step
            title="גישה לאנשי הקשר"
            description="בלי זה אנדרואיד לא יזהה לקוחות שכבר שמורים אצלך באנשי הקשר"
            :done="!!status?.contacts"
            :loading="busyStep === 'contacts'"
            @action="runStep('contacts', () => CallerId.requestContactsPermission())"
          />
          <setup-step
            title="התראות"
            description="התראה עם התור הבא — לחיצה פותחת את כרטיס הלקוח"
            :done="!!status?.notifications"
            :loading="busyStep === 'notifications'"
            @action="runStep('notifications', () => CallerId.requestNotificationPermission())"
          />
          <setup-step
            title="הצגה מעל אפליקציות אחרות"
            description="כרטיס לקוח צף מעל מסך השיחה (מומלץ)"
            :done="!!status?.overlay"
            :loading="busyStep === 'overlay'"
            @action="runStep('overlay', () => CallerId.openOverlaySettings())"
          />

          <settings-toggle-row
            title="כרטיס צף מעל מסך השיחה"
            :status="settings.showOverlay"
            :disabled="busy"
            @value-changed="(v: boolean) => onDisplayOptionChanged({ showOverlay: v })"
          />
          <settings-toggle-row
            title="התראה עם פרטי הלקוח"
            :status="settings.showNotification"
            :disabled="busy"
            @value-changed="(v: boolean) => onDisplayOptionChanged({ showNotification: v })"
          />
          <settings-toggle-row
            title="הצגה גם במחשב"
            description="כשהדשבורד של TimeIt פתוח במחשב, יקפוץ שם אותו כרטיס לקוח"
            :status="settings.notifyDesktop"
            :disabled="busy"
            @value-changed="(v: boolean) => onDisplayOptionChanged({ notifyDesktop: v })"
          />
        </template>

        <!-- iOS: one switch in the Settings app; iOS shows our label on the call screen. -->
        <template v-else-if="platform === 'ios' && status?.supported">
          <setup-step
            title="הפעלה בהגדרות האייפון"
            description="הגדרות ← טלפון ← חסימה וזיהוי שיחות ← להפעיל את TimeIt"
            :done="!!status?.extensionEnabled"
            :loading="busyStep === 'ios-settings'"
            action-label="פתיחת ההגדרות"
            @action="runStep('ios-settings', () => CallerId.openSettings())"
          />
          <div class="py-3 border-b border-surface-100 text-xs text-surface-500 leading-relaxed">
            באייפון יופיע מתחת למספר המתקשר/ת: שם הלקוח, התור הבא וחוב פתוח (אם יש).
            לחיצה על הלקוח בהיסטוריית השיחות לא פותחת את TimeIt — זו מגבלה של אפל.
          </div>
        </template>

        <div v-else-if="status && !status.supported" class="py-3 border-b border-surface-100 text-xs text-surface-500">
          {{ unsupportedText }}
        </div>

        <div class="flex items-center gap-3 py-3">
          <div class="flex-1 min-w-0 text-xs text-surface-500 leading-relaxed">
            <div>{{ syncLine }}</div>
            <div>הרשימה נשמרת רק במכשיר הזה ונמחקת בהתנתקות.</div>
          </div>
          <button
            v-if="platform === 'android' && status?.ready && (status?.cachedEntries ?? 0) > 0"
            type="button"
            class="text-xs font-medium text-primary-600 flex-none"
            @click="runStep('preview', () => CallerId.preview())"
          >הצגת דוגמה</button>
          <button
            type="button"
            class="text-xs font-medium text-primary-600 flex items-center gap-1.5 flex-none"
            :disabled="syncing"
            @click="onSyncNow"
          >
            <i class="pi pi-refresh text-xs" :class="{ 'pi-spin': syncing }" />
            רענון
          </button>
        </div>
      </template>
    </settings-card>
  </div>
</template>

<script setup lang="ts">
import {computed, h, onMounted, onUnmounted, ref} from 'vue';
import {App as CapacitorApp} from '@capacitor/app';
import type {PluginListenerHandle} from '@capacitor/core';
import {useToast} from 'vue-toastification';

import {useAdminStore} from '@app/stores';
import {trackEvent} from '@app/composables/posthog';
import {CallerId, ICallerIdSettings, ICallerIdStatus, useCallerId} from '@app/composables/caller-id';
import SettingsCard from './ui/SettingsCard.vue';
import SettingsGroupLabel from './ui/SettingsGroupLabel.vue';
import SettingsToggleRow from './ui/SettingsToggleRow.vue';

const adminStore = useAdminStore();
const toast = useToast();
const {
  isAvailable, platform, status, settings, syncing,
  loadSettings, saveSettings, refreshStatus, sync, enable, disable
} = useCallerId();

const busy = ref(false);
const busyStep = ref<string | null>(null);

// The directory maps phone numbers to names, so it follows the same rule as the rest of
// the app: users who aren't allowed to see customer contact details don't get it.
const visible = computed(() => isAvailable && adminStore.getCanViewCustomerContact);

const mainDescription = computed(() => platform === 'ios'
  ? 'כשלקוח/ה מתקשר/ת, יופיעו על מסך השיחה השם והתור הבא שלהם'
  : 'כשלקוח/ה מתקשר/ת, יופיע כרטיס עם השם, התור הבא וחוב פתוח — לחיצה פותחת את כרטיס הלקוח');

const unsupportedText = computed(() => platform === 'ios'
  ? 'הגרסה הזו של האפליקציה עדיין לא תומכת בזיהוי שיחות. עדכנו את האפליקציה מה-App Store.'
  : 'זיהוי שיחות דורש אנדרואיד 10 ומעלה.');

const syncLine = computed(() => {
  const count = status.value?.cachedEntries ?? 0;
  const syncedAt = status.value?.syncedAt;
  if (!syncedAt) return 'עדיין לא סונכרנו לקוחות';
  const time = new Date(syncedAt).toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'});
  const day = new Date(syncedAt).toDateString() === new Date().toDateString()
    ? 'היום' : new Date(syncedAt).toLocaleDateString('he-IL');
  return `${count} לקוחות מסונכרנים · עודכן ${day} ב-${time}`;
});

const onToggleEnabled = async (value: boolean) => {
  busy.value = true;
  trackEvent(value ? 'caller_id_enabled' : 'caller_id_disabled', {platform});
  try {
    if (value) {
      const result = await enable();
      if (!result) toast.error('לא הצלחנו לסנכרן את רשימת הלקוחות');
      else await promptFirstStep(result);
    } else {
      await disable();
    }
  } finally {
    busy.value = false;
  }
};

// Right after switching on, jump straight into the one required step.
const promptFirstStep = async (current: ICallerIdStatus) => {
  if (current.ready || !current.supported) return;
  if (platform === 'android') await runStep('role', () => CallerId.requestScreeningRole());
  else if (platform === 'ios') await runStep('ios-settings', () => CallerId.openSettings());
};

const runStep = async (step: string, action: () => Promise<ICallerIdStatus>) => {
  busyStep.value = step;
  trackEvent('caller_id_setup_step', {step, platform});
  try {
    status.value = await action();
  } catch (e) {
    console.error(`[caller-id] ${step} failed`, e);
    toast.error('הפעולה לא הושלמה, נסו שוב');
  } finally {
    busyStep.value = null;
  }
};

const onDisplayOptionChanged = async (patch: Partial<ICallerIdSettings>) => {
  await saveSettings(patch);
  await sync({force: true});
};

const onSyncNow = async () => {
  trackEvent('caller_id_sync_clicked', {platform});
  const result = await sync({force: true});
  if (result) toast.success('רשימת הלקוחות עודכנה');
  else toast.error('לא הצלחנו לסנכרן את רשימת הלקוחות');
};

// Overlay permission and the iOS Settings switch have no callback — re-read on return.
let resumeListener: PluginListenerHandle | undefined;
onMounted(async () => {
  if (!visible.value) return;
  await loadSettings();
  await refreshStatus();
  resumeListener = await CapacitorApp.addListener('appStateChange', ({isActive}) => {
    if (isActive) refreshStatus();
  });
});
onUnmounted(() => resumeListener?.remove());

/** A setup row: title + explanation, then a ✓ when done or an action button. */
const SetupStep = (props: {
  title: string; description: string; done: boolean; loading?: boolean; actionLabel?: string
}, {emit}: { emit: (event: 'action') => void }) =>
  h('div', {class: 'flex items-center gap-3 py-3 border-b border-surface-100'}, [
    h('div', {class: 'flex-1 min-w-0'}, [
      h('div', {class: 'text-sm font-medium'}, props.title),
      h('div', {class: 'text-xs text-surface-500 mt-1 leading-relaxed'}, props.description),
    ]),
    props.done
      ? h('span', {class: 'flex items-center gap-1 text-xs font-semibold text-green-700 flex-none'}, [
        h('i', {class: 'pi pi-check-circle text-sm'}), 'מוגדר',
      ])
      : h('button', {
        type: 'button',
        class: 'text-xs font-semibold text-white bg-primary rounded-full px-3 py-1.5 flex-none flex items-center gap-1.5',
        disabled: props.loading,
        onClick: () => emit('action'),
      }, [
        props.loading ? h('i', {class: 'pi pi-spinner pi-spin text-xs'}) : null,
        props.actionLabel ?? 'הפעלה',
      ]),
  ]);
SetupStep.props = ['title', 'description', 'done', 'loading', 'actionLabel'];
SetupStep.emits = ['action'];
</script>
